//accept-invite-page.tsx

import React, { useState, useContext } from "react";
import {
  TextField,
  Box,
  Button,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { LogoHeader } from "../components/logo-header";
import { validationMessages } from "../utils/validation/messages";
import { useHistory, useLocation } from "react-router-dom";
import { ErrorContext } from "../components/app-routes";
import axios from "axios";
import { growerInvitesApi } from "../services/api/grower-invites";
import { sessionsApi } from "../services/api/sessions";

export function AcceptInvitePage() {
  const location = useLocation();
  const history = useHistory();
  const { handleError } = useContext(ErrorContext);

  const [busy, setBusy] = useState<boolean>(false);

  const form = useFormik({
    initialValues: {
      name: "",
      password1: "",
      password2: "",
    },
    validationSchema: yup.object({
      name: yup.string().max(100, "Name is too long").optional(),
      password1: yup
        .string()
        .required(validationMessages.Required)
        .min(8, "Password must be at least 8 characters"),
      password2: yup
        .string()
        .required(validationMessages.Required)
        .equals([yup.ref("password1")], "Must be equal"),
    }),
    onSubmit: () => {
      acceptInvite();
    },
  });

  const acceptInvite = async () => {
    setBusy(true);
    try {
      const search = new URLSearchParams(location.search);
      const idStr = search.get("id");
      const token = search.get("token");

      if (!idStr || !token) {
        alert("Missing invite link parameters.");
        return;
      }

      const id = Number(idStr);
      if (!Number.isFinite(id) || id <= 0) {
        alert("Invalid invite id.");
        return;
      }

      await growerInvitesApi.accept(
        id,
        token,
        form.values.password1,
        form.values.name || undefined,
      );

      try {
        await sessionsApi.end();
      } catch (error) {
        // ignore
      }

      alert("Invite accepted! Please log in.");

      try {
        await sessionsApi.end();
      } catch (error) {
        // ignore
      }

      window.location.assign("/log-in");
    } catch (error) {
      // nice UX for common cases
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if (status === 401) {
          alert(
            "That invite link is invalid. Please ask the farm owner to resend it.",
          );
          history.replace("/log-in");
          return;
        }
        if (status === 404) {
          alert(
            "That invite was not found. Please ask the farm owner to resend it.",
          );
          history.replace("/log-in");
          return;
        }
        if (status === 410) {
          alert(
            "That invite expired or was revoked. Please ask the farm owner to resend it.",
          );
          history.replace("/log-in");
          return;
        }
        if (status === 409) {
          alert("That invite has already been accepted. Please log in.");
          history.replace("/log-in");
          return;
        }
      }

      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <LogoHeader />
      <Paper elevation={3}>
        <Box sx={{ mt: 4, p: 3, textAlign: "center" }}>
          <Typography gutterBottom variant="h5" component="h2">
            Accept Invite
          </Typography>
          <Typography variant="subtitle1">
            Set a password to join the team
          </Typography>

          <form onSubmit={form.handleSubmit} noValidate>
            <Box mt={2}>
              <TextField
                id="name"
                name="name"
                label="Name (optional)"
                value={form.values.name}
                onChange={form.handleChange}
                fullWidth
                variant="outlined"
                autoFocus
                error={form.touched.name && Boolean(form.errors.name)}
                helperText={form.touched.name && form.errors.name}
              />
            </Box>

            <Box mt={2}>
              <TextField
                id="password1"
                name="password1"
                type="password"
                label="Password"
                value={form.values.password1}
                onChange={form.handleChange}
                fullWidth
                variant="outlined"
                error={form.touched.password1 && Boolean(form.errors.password1)}
                helperText={form.touched.password1 && form.errors.password1}
              />
            </Box>

            <Box mt={2}>
              <TextField
                id="password2"
                name="password2"
                type="password"
                label="Confirm Password"
                value={form.values.password2}
                onChange={form.handleChange}
                fullWidth
                variant="outlined"
                error={form.touched.password2 && Boolean(form.errors.password2)}
                helperText={form.touched.password2 && form.errors.password2}
              />
            </Box>

            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={busy}
                fullWidth
              >
                Accept Invite
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Container>
  );
}
